import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react'
import { CircularProgress, Stack } from '@mui/material'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { registerAccessTokenMiddleware } from '../api'
import { setFaroSessionId } from '../faro/faroSession'
import { bool } from '../utils/utils'
import { AuthenticationContextValues, AuthenticationContextValuesImpl } from './AuthenticationContextValues'
import { mockAuthContextValues } from './authMocking'

const enableAuthMocking = bool(import.meta.env.VITE_ENABLE_AUTH_MOCKING)

const initialContextValues: AuthenticationContextValues = new AuthenticationContextValuesImpl(undefined, [])

interface JwtScope {
  scope?: string
}

export const AuthenticationContext = createContext<AuthenticationContextValues>(enableAuthMocking ? mockAuthContextValues : initialContextValues)
export const useAuthenticationContext = () => useContext(AuthenticationContext)

const AuthenticationContextProviderHelper: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0()
  const [accessTokenMiddlewareInitialized, setAccessTokenMiddlewareInitialized] = useState(false)
  const [authenticationContextValues, setAuthenticationContextValues] = useState<AuthenticationContextValues>(
    enableAuthMocking ? mockAuthContextValues : initialContextValues
  )
  const currentApiContextValues = useRef<AuthenticationContextValues | null>(null)
  const sessionIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (isAuthenticated && !accessTokenMiddlewareInitialized && !enableAuthMocking) {
      // Generate session ID once when user authenticates
      if (!sessionIdRef.current) {
        sessionIdRef.current = crypto.randomUUID()
        setFaroSessionId(sessionIdRef.current)
      }

      const getAccessToken = async (usePopup?: boolean) => {
        try {
          let token: string | undefined
          if (usePopup) {
            token = await getAccessTokenWithPopup()
          } else {
            token = await getAccessTokenSilently()
          }

          if (token && token !== currentApiContextValues.current?.accessToken) {
            const decodedToken: JwtPayload & JwtScope = jwtDecode(token)
            const jwtScopes = decodedToken?.scope?.split(' ') ?? []
            const devExtraScopes =
              import.meta.env.DEV && typeof import.meta.env.VITE_DEV_ADD_SCOPES === 'string'
                ? import.meta.env.VITE_DEV_ADD_SCOPES.split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
                : []

            currentApiContextValues.current = new AuthenticationContextValuesImpl(
              token,
              [...new Set([...jwtScopes, ...devExtraScopes])],
              sessionIdRef.current ?? undefined
            )
            setAuthenticationContextValues(currentApiContextValues.current)
          }

          if (token) {
            return token
          } else {
            throw new Error('No access token acquired')
          }
        } catch (e) {
          const error = e as { error: string }

          // While running in local, Auth0 may require consent via the popup. This code detects that case and retries the call with the popup.
          if (import.meta.env.DEV && error.error === 'consent_required' && !usePopup) {
            return await getAccessToken(true)
          }
          throw e
        }
      }

      // This is how the museum client gets its access tokens injected into each request
      registerAccessTokenMiddleware(() => getAccessToken())

      // Get the initial access token and set initialized to true
      getAccessToken().then(() => setAccessTokenMiddlewareInitialized(true))
    }
  }, [isAuthenticated, getAccessTokenSilently, getAccessTokenWithPopup, accessTokenMiddlewareInitialized])

  if ((isAuthenticated && accessTokenMiddlewareInitialized) || enableAuthMocking) {
    return <AuthenticationContext.Provider value={authenticationContextValues}>{children}</AuthenticationContext.Provider>
  } else {
    return (
      <Stack
        useFlexGap
        sx={{
          blockSize: '100vh',
          inlineSize: '100vw',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-surface-low)'
        }}
      >
        <CircularProgress />
      </Stack>
    )
  }
}

export const AuthenticationContextProvider = enableAuthMocking
  ? AuthenticationContextProviderHelper
  : withAuthenticationRequired(AuthenticationContextProviderHelper)
